/** Calendar — v1 parity: agenda tabs (Today / Tomorrow / This Week / This
 * Month) + month grid ("Calendar"), merging user events, open task due dates
 * and deal closings (v1 _calEventsAll), plus v1's New Event modal (Title,
 * Type, All day, Start/End Date+Time, Location, Description; title + start
 * date required; label = title [+ time]). Events write as whole-group
 * replaces of group 'cal' through the Phase 7 door. */
import { useMemo, useState } from "react";
import {
  CAL_COLORS, CAL_GROUP, CAL_TYPES, agendaFor, buildEventLabel, dayHeader,
  monthGrid, monthTitle, todayStamp, type CalEvent, type CalGroups, type CalItem, type CalRange,
} from "./api";
import type { TaskRec } from "../edits/api";
import type { Deal } from "../deals/api";
import { mergeCalendarItems } from "./api";
import { backendFetch } from "../../lib/backend";

const RANGES: { key: CalRange; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "tomorrow", label: "Tomorrow" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "grid", label: "Calendar" },
];

const RANGE_EMPTY: Record<string, string> = {
  today: "today", tomorrow: "tomorrow", week: "this week", month: "this month",
};

/** kind → dot/pill classes (v1: event blue, task amber, closing emerald). */
const KIND_CLS: Record<CalItem["kind"], string> = { event: "blue", task: "amber", closing: "emerald" };

export function CalendarPage({
  groups,
  tasks,
  deals,
  onSaveGroup,
  me = null,
  token = null,
}: {
  groups: CalGroups;
  tasks: TaskRec[];
  deals: Deal[];
  /** persist a whole-group replace (app enqueues the op + updates state) */
  onSaveGroup: (group: string, events: CalEvent[]) => void;
  /** signed-in user — new events are stamped as HIS so the 1-hour-before
   * reminder alerts him only (Tom 2026-08-19). */
  me?: { id: string; name: string } | null;
  /** backend token for the reminder snooze/move endpoint */
  token?: string | null;
}) {
  const today = todayStamp();
  const [range, setRange] = useState<CalRange>("today");
  const [view, setView] = useState<{ y: number; m: number }>({ y: today.y, m: today.m });
  const [adding, setAdding] = useState(false);
  // event whose reminder is being snoozed/moved (Tom 2026-08-19: "change to a
  // different hour with the snooze") — click the ⏰ on an agenda row
  const [snoozeEv, setSnoozeEv] = useState<CalEvent | null>(null);

  const items = useMemo(() => mergeCalendarItems(groups, tasks, deals), [groups, tasks, deals]);
  const agenda = useMemo(() => agendaFor(items, range, today), [items, range, today.y, today.m, today.d]);

  const nav = (delta: number) => {
    if (delta === 0) {
      setView({ y: today.y, m: today.m });
      return;
    }
    let m = view.m + delta;
    let y = view.y;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setView({ y, m });
  };

  const cells = useMemo(() => monthGrid(view.y, view.m, today), [view.y, view.m, today.y, today.m, today.d]);
  const gridEvents = useMemo(() => {
    const by = new Map<number, CalItem[]>();
    for (const i of items) {
      if (i.day.y !== view.y || i.day.m !== view.m) continue;
      const arr = by.get(i.day.d) ?? [];
      arr.push(i);
      by.set(i.day.d, arr);
    }
    return by;
  }, [items, view.y, view.m]);

  const saveEvent = (ev: CalEvent) => {
    // stamp the creator — the reminder is his alone, and the agenda shows
    // whose event it is
    if (me?.id) {
      ev.by = me.id;
      if (me.name) ev.byName = me.name;
    }
    onSaveGroup(CAL_GROUP, [...(groups[CAL_GROUP] ?? []), ev]);
    setAdding(false);
  };

  return (
    <div className="calendar">
      <header className="bar">
        <div className="caltitle">
          <h1>Calendar</h1>
          <span className="sub">Showings, deadlines, inspections, and events</span>
        </div>
        <button className="btn" onClick={() => setAdding(true)}>+ Add Event</button>
      </header>

      <div className="caltabs">
        {RANGES.map((r) => (
          <button key={r.key} className={"caltab" + (range === r.key ? " active" : "")} onClick={() => setRange(r.key)}>
            {r.label}
          </button>
        ))}
      </div>

      {range !== "grid" ? (
        agenda.length === 0 ? (
          <div className="empty calempty">
            <div className="calemptyhead">Nothing scheduled {RANGE_EMPTY[range]}</div>
            <div className="sub">Events, task due dates and deal closings show up here.</div>
          </div>
        ) : (
          <div className="calagenda">
            {agenda.map((g) => (
              <div key={`${g.day.y}-${g.day.m}-${g.day.d}`} className="calday">
                <div className="caldayhead">{dayHeader(g.day)}</div>
                {g.items.map((i, ix) => (
                  <div key={ix} className="calrow">
                    <span className={"caldot dot-" + KIND_CLS[i.kind]} />
                    <span className="callabel">
                      {i.label}
                      {i.ev?.byName ? <span className="sub"> · {i.ev.byName.split(" ")[0]}</span> : null}
                      {i.ev?.remind ? (
                        <button
                          className="dlink"
                          title="Alerts 1 hour before — click to move the reminder"
                          onClick={() => setSnoozeEv(i.ev ?? null)}
                        > ⏰</button>
                      ) : null}
                    </span>
                    <span className={"calpill pill-" + KIND_CLS[i.kind]}>{i.kind}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="calgridwrap">
          <div className="calgridbar">
            <strong>{monthTitle(view.y, view.m)}</strong>
            <div className="calnav">
              <button className="out" onClick={() => nav(0)}>Today</button>
              <button className="out" onClick={() => nav(-1)}>‹</button>
              <button className="out" onClick={() => nav(1)}>›</button>
            </div>
          </div>
          <div className="calgridhead">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          <div className="calgrid">
            {cells.map((c, i) => (
              <div key={i} className={"calcell" + (c.day === null ? " pad" : "")}>
                {c.day !== null ? (
                  <span className={"calnum" + (c.isToday ? " today" : "")}>{c.day}</span>
                ) : null}
                {c.day !== null
                  ? (gridEvents.get(c.day) ?? []).slice(0, 3).map((e, ix) => {
                      const hue = e.kind === "event" ? (CAL_COLORS[e.ev?.type ?? "Other"] ?? "slate") : KIND_CLS[e.kind];
                      return (
                        <div key={ix} className={"calchip chip-" + hue} title={e.label}>
                          {e.label}
                        </div>
                      );
                    })
                  : null}
                {c.day !== null && (gridEvents.get(c.day)?.length ?? 0) > 3 ? (
                  <div className="calmore">+{(gridEvents.get(c.day)?.length ?? 0) - 3} more</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {adding ? <NewEventModal viewY={view.y} viewM={view.m} onCancel={() => setAdding(false)} onSave={saveEvent} /> : null}
      {snoozeEv ? <SnoozeModal ev={snoozeEv} token={token ?? null} onClose={() => setSnoozeEv(null)} /> : null}
    </div>
  );
}

/** Move a reminder (Tom 2026-08-19: "remind me in 30 mins, 1 hour etc" +
 * "change to a different hour"). Quick snoozes or an exact date+time; the
 * backend keys the reminder by the event's own fields, so this works whether
 * or not the alert has fired yet — a moved time REPLACES the 1-hour-before. */
function SnoozeModal({ ev, token, onClose }: { ev: CalEvent; token: string | null; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [at, setAt] = useState("");
  const send = async (payload: { minutes?: number; at?: string }): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await backendFetch<{ ok: boolean; next: string }>(
        token, "/api/notifications/cal-snooze",
        {
          method: "POST",
          body: {
            ev: {
              g: CAL_GROUP, y: ev.y, m: ev.m, d: ev.d,
              title: ev.title ?? ev.label ?? "", startTime: ev.startTime ?? "", by: ev.by ?? "",
            },
            ...payload,
          },
        });
      const when = new Date(r.next + (r.next.endsWith("Z") ? "" : "Z"));
      setMsg("Reminder moved to " + when.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + ".");
    } catch (e) {
      setMsg("Couldn't move it — " + (e instanceof Error ? e.message : "try again shortly"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="calmodalwrap" onClick={onClose}>
      <div className="calmodal" onClick={(e) => e.stopPropagation()}>
        <div className="calmodalhead">
          <h2>Move reminder</h2>
          <button className="taskdel" onClick={onClose}>×</button>
        </div>
        <div className="calmodalbody">
          <div className="sub" style={{ marginBottom: 8 }}>{ev.title ?? ev.label}</div>
          {msg ? (
            <div style={{ marginBottom: 8 }}>{msg}</div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                <button className="out" disabled={busy} onClick={() => void send({ minutes: 30 })}>+30 min</button>
                <button className="out" disabled={busy} onClick={() => void send({ minutes: 60 })}>+1 hour</button>
                <button className="out" disabled={busy} onClick={() => void send({ minutes: 180 })}>+3 hours</button>
              </div>
              <div className="arvfield">
                <label className="lbl">Or pick the exact time</label>
                <input className="moneyinput" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
              </div>
            </>
          )}
        </div>
        <div className="calmodalfoot">
          <button className="out" onClick={onClose}>{msg ? "Done" : "Cancel"}</button>
          {!msg ? (
            <button
              className="btn"
              disabled={busy || !at}
              onClick={() => { const d = new Date(at); if (!Number.isNaN(d.getTime())) void send({ at: d.toISOString() }); }}
            >
              {busy ? "Moving…" : "Move reminder"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** v1's New Event modal, field for field. */
function NewEventModal({
  viewY,
  viewM,
  onCancel,
  onSave,
}: {
  viewY: number;
  viewM: number;
  onCancel: () => void;
  onSave: (ev: CalEvent) => void;
}) {
  const t = new Date();
  const inMonth = viewM === t.getMonth() && viewY === t.getFullYear();
  const startIso = `${viewY}-${String(viewM + 1).padStart(2, "0")}-${String(inMonth ? t.getDate() : 1).padStart(2, "0")}`;
  const [title, setTitle] = useState("");
  const [type, setType] = useState("Other");
  const [allDay, setAllDay] = useState(false);
  const [sDate, setSDate] = useState(startIso);
  const [sTime, setSTime] = useState("09:00");
  const [eDate, setEDate] = useState("");
  const [eTime, setETime] = useState("10:00");
  const [loc, setLoc] = useState("");
  const [desc, setDesc] = useState("");
  const [remind, setRemind] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = () => {
    if (!title.trim()) {
      setErr("Title is required");
      return;
    }
    if (!sDate) {
      setErr("Start date is required");
      return;
    }
    const [y, m, d] = sDate.split("-").map(Number);
    if (!y || !m || !d) {
      setErr("Start date is required");
      return;
    }
    const ev: CalEvent = {
      y, m: m - 1, d,
      label: buildEventLabel(title.trim(), allDay, sTime),
      type, title: title.trim(), allDay,
    };
    if (!allDay && sTime) ev.startTime = sTime;
    if (eDate) ev.endDate = eDate;
    if (!allDay && eTime) ev.endTime = eTime;
    if (loc.trim()) ev.location = loc.trim();
    if (desc.trim()) ev.desc = desc.trim();
    if (remind) ev.remind = true;
    onSave(ev);
  };

  return (
    <div className="calmodalwrap" onClick={onCancel}>
      <div className="calmodal" onClick={(e) => e.stopPropagation()}>
        <div className="calmodalhead">
          <h2>New Event</h2>
          <button className="taskdel" onClick={onCancel}>×</button>
        </div>
        <div className="calmodalbody">
          <div className="arvfield">
            <label className="lbl">Title</label>
            <input className="moneyinput" autoFocus placeholder="Event title" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <div className="arvfield">
            <label className="lbl">Event Type</label>
            <select className="moneyinput" value={type} onChange={(e) => setType(e.target.value)}>
              {CAL_TYPES.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>
          <label className="calallday">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> All day
          </label>
          <label className="calallday" title="You'll get an app + phone notification, with snooze options if you can't do it right then.">
            <input type="checkbox" checked={remind} onChange={(e) => setRemind(e.target.checked)} />
            {" "}Alert me 1 hour before{allDay ? " (8:00 AM that day)" : ""}
          </label>
          <div className="calmodalgrid">
            <div className="arvfield">
              <label className="lbl">Start Date</label>
              <input className="moneyinput" type="date" value={sDate} onChange={(e) => setSDate(e.target.value)} />
            </div>
            {!allDay ? (
              <div className="arvfield">
                <label className="lbl">Start Time</label>
                <input className="moneyinput" type="time" value={sTime} onChange={(e) => setSTime(e.target.value)} />
              </div>
            ) : null}
            <div className="arvfield">
              <label className="lbl">End Date</label>
              <input className="moneyinput" type="date" value={eDate} onChange={(e) => setEDate(e.target.value)} />
            </div>
            {!allDay ? (
              <div className="arvfield">
                <label className="lbl">End Time</label>
                <input className="moneyinput" type="time" value={eTime} onChange={(e) => setETime(e.target.value)} />
              </div>
            ) : null}
          </div>
          <div className="arvfield">
            <label className="lbl">Location</label>
            <input className="moneyinput" placeholder="Optional location" value={loc} onChange={(e) => setLoc(e.target.value)} />
          </div>
          <div className="arvfield">
            <label className="lbl">Description</label>
            <textarea className="notes calnotes" rows={3} placeholder="Optional description" value={desc} onChange={(e) => setDesc(e.target.value)} />
          </div>
          {err ? <div className="calerr">{err}</div> : null}
        </div>
        <div className="calmodalfoot">
          <button className="out" onClick={onCancel}>Cancel</button>
          <button className="btn" onClick={save}>+ Create Event</button>
        </div>
      </div>
    </div>
  );
}
